import { Link } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import { MessageCircle, Mail, MapPin, Phone } from "lucide-react";

const WA_URL = "https://wa.me/436641004293?text=" + encodeURIComponent("Hallo! Ich interessiere mich für eine Buchung im Chalet Steiermark.");

export default function Footer() {
  const { t, lang } = useLang();
  return (
    <footer data-testid="site-footer" className="relative bg-[hsl(185_32%_11%)] text-white overflow-hidden">
      {/* Decorative top accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[80rem] h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent))]/35 to-transparent" />
      <div className="absolute -top-px left-1/2 -translate-x-1/2 w-32 h-px bg-[hsl(var(--accent))]" />

      <div className="container-x py-20 md:py-28">
        {/* Top brand row + CTA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-16 pb-14 border-b border-white/10">
          <div className="lg:col-span-7">
            <div className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
              Chalet <span className="italic text-[hsl(var(--accent))]">Steiermark</span>
              <span className="block mt-2 font-light text-3xl md:text-4xl text-white/80">· Landhaus</span>
            </div>
            <p className="mt-7 font-serif italic text-xl md:text-2xl text-white/75 max-w-xl leading-snug">
              {t.footer.tag}
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/book" data-testid="footer-book" className="btn-primary justify-between !text-base !py-5 !px-7">
              <span>{lang === "de" ? "Jetzt buchen" : "Book now"}</span>
              <span aria-hidden>→</span>
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center justify-between gap-3 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-white px-7 py-5 transition-colors"
            >
              <span className="inline-flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[#25D366]" strokeWidth={2.25} />
                <span className="text-[12px] tracking-[0.18em] uppercase font-semibold">{lang === "de" ? "WhatsApp-Anfrage" : "WhatsApp inquiry"}</span>
              </span>
              <span className="text-sm text-white/75">+43 664 1004293</span>
            </a>
          </div>
        </div>

        {/* Lower link grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-4">
            <div className="overline !text-white/55 mb-5">{t.footer.contact}</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.8} />
                <span>Pusterwald · Steiermark · Österreich</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.8} />
                <a href="mailto:office@chalet-steiermark.com" className="hover:text-[hsl(var(--accent))] transition-colors">
                  office@chalet-steiermark.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.8} />
                <a href="tel:+436641004293" className="hover:text-[hsl(var(--accent))] transition-colors">
                  +43 664 1004293
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline !text-white/55 mb-5">{t.footer.quick}</div>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li><Link to="/" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/book" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.book}</Link></li>
              <li><Link to="/#units" className="hover:text-[hsl(var(--accent))] transition-colors">Chalet & Landhaus</Link></li>
              <li><Link to="/#gallery" className="hover:text-[hsl(var(--accent))] transition-colors">{lang === "de" ? "Galerie" : "Gallery"}</Link></li>
              <li><Link to="/#location" className="hover:text-[hsl(var(--accent))] transition-colors">{lang === "de" ? "Lage" : "Location"}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline !text-white/55 mb-5">{t.footer.legal}</div>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li className="hover:text-[hsl(var(--accent))] cursor-pointer transition-colors">{t.footer.impressum}</li>
              <li className="hover:text-[hsl(var(--accent))] cursor-pointer transition-colors">{t.footer.datenschutz}</li>
              <li className="hover:text-[hsl(var(--accent))] cursor-pointer transition-colors">{t.footer.agb}</li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline !text-white/55 mb-5">{lang === "de" ? "Vermieter" : "Hosts"}</div>
            <ul className="space-y-2 text-xs text-white/70 leading-relaxed">
              <li>Waltraud Fernberg</li>
              <li className="text-white/45">Zirben Chalet</li>
              <li className="pt-2">Royal Living Handels GmbH</li>
              <li className="text-white/45">Landhaus Steiermark</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs tracking-[0.18em] uppercase">
          <div className="text-white/45">© {new Date().getFullYear()} Chalet Steiermark · {t.footer.rights}</div>
          <div className="text-white/40">{lang === "de" ? "Mit Sorgfalt gemacht in der Steiermark" : "Made with care in Styria"}</div>
        </div>
      </div>
    </footer>
  );
}
